import { MouseEvent, useEffect, useState } from 'react';
import useProductCardImage from '~/hooks/components/useProductCardImage';
import GenericProductCard from '~/components/GenericProductCard';
import { navigate } from 'gatsby';
import useVariantAnalytics from '~/hooks/utils/customAnalytics/use-variant-analytics';
import { ThemeUIStyleObject } from 'theme-ui';
import { Image } from '~/types';
import { useBadge } from '~/hooks/components/use-badge';

type Props = {
  product?: Queries.ProductCardFragmentFragment;
  forwardSx?: ThemeUIStyleObject;
  wrapperSx?: ThemeUIStyleObject;
  badgeColor?: string;
  mainImage: Image;
  featuredImage: Image;
  cardMicroloadHighlight?: string;
  colorSelect?: boolean;
  isFeatured?: boolean;
  bgColor?: string;
  collectionSlug?: string;
  renderPrice?: boolean;
  cardDirection?: boolean | null;
  disableHoverChange?: boolean;
  cdpCopy?: string;
};

const ProductCardAlternate = ({
  product,
  forwardSx = {},
  wrapperSx = {},
  mainImage,
  featuredImage,
  cardMicroloadHighlight,
  colorSelect = true,
  isFeatured = false,
  bgColor = 'white',
  collectionSlug,
  renderPrice,
  cardDirection,
  disableHoverChange = true, // If true images do not change and color swatch does not prevent default
  cdpCopy,
}: Props) => {
  // for variant color selector card
  const [variant, setVariant] = useState<Queries.VariantDetailsFragmentFragment | undefined | null>(
    product?.variants?.[0]
  );
  const badges = product?.badges;
  const badge = useBadge({ selectedOption: variant, badges: badges as Queries.BadgeFragment[] });
  const [queryString, setQueryString] = useState('');
  const [hoverStart, setHoverStart] = useState(false);

  /**
   *  DESKTOP:
   *  If featured, and if hoverStart:
   *  pass variant featuredImage when available, or variant main image *if not available,
   *  if !hoverStart: pass product featuredImage if available or mainImage or
   *  mainImage.
   * Else if (hoverStart) pass variant main image if available, else pass
   * product cdpmobile image or product cdp image if not available.
   */

  const getDesktopImage = () => {
    if (isFeatured) {
      if (hoverStart && !disableHoverChange) {
        return variant?.mainImage;
      } else {
        return featuredImage ?? mainImage;
      }
    } else {
      if (hoverStart && !disableHoverChange) {
        return variant?.mainImage;
      } else {
        return mainImage;
      }
    }
  };
  const desktopImage = getDesktopImage();

  /**
   * MOBILE:
   * If hoverStart pass variant main image else pass card mainImage or
   * mainImage if not available.
   */
  const getMobileImage = () => {
    if (hoverStart && !disableHoverChange) {
      return variant?.mainImage;
    } else {
      return mainImage;
    }
  };
  const mobileImage = getMobileImage();

  const { productCardImage } = useProductCardImage({
    desktopImage,

    mobileImage,
  });

  const [hoverImage, setCurrentImage] = useState(productCardImage);

  const { trackVariantClicked } = useVariantAnalytics(product?.slug);
  useEffect(() => {
    if (disableHoverChange) return;

    const timer = window.setTimeout(() => {
      setCurrentImage(productCardImage);
    }, 300);

    return () => clearTimeout(timer);
  }, [disableHoverChange, productCardImage]);

  const currentImage = disableHoverChange ? productCardImage : hoverImage;

  const colorSwitcherCallback = (variant: Queries.VariantDetailsFragmentFragment, query: string, event: MouseEvent) => {
    setVariant(variant);
    setQueryString(query);

    if (disableHoverChange && event.type !== 'mouseenter') {
      // Only navigate if a click in the swatch occured and if the hover color feature is disabled
      trackVariantClicked({ locationDetail: 'color_swatch', collection: collectionSlug, variant: variant });
      navigate(`/${collectionSlug}/${product?.slug}/${query.length ? `?${query}/` : ''}`);
    }
    setHoverStart(true);
  };

  return (
    <GenericProductCard
      isFeatured={isFeatured}
      name={product?.name ?? ''}
      title={product?.longTitle ?? ''}
      collectionSlug={collectionSlug}
      slug={product?.slug ?? ''}
      queryString={queryString}
      bgColor={bgColor}
      forwardSx={forwardSx}
      wrapperSx={wrapperSx}
      price={variant?.price ?? 0}
      regularPrice={variant?.regularPrice ?? 0}
      badge={badge}
      callback={colorSwitcherCallback}
      currentImage={currentImage}
      cardMicroloadHighlight={cardMicroloadHighlight}
      colorSelect={colorSelect}
      cdpCopy={cdpCopy}
      load={false}
      product={product}
      renderPrice={renderPrice}
      cardDirection={cardDirection}
    />
  );
};

export default ProductCardAlternate;
